import { subscribeToSSE } from "../eventsClient/sse";
import { getBackendURL } from "../getBaseUrl";
import { type ChiefLiveEvent, ChiefLiveEventSchema } from "./chiefClient.types";

export interface ChiefLiveSubscriptionOptions {
  authToken: () => string | undefined;
  projectId: () => string | undefined;
  onEvent: (event: ChiefLiveEvent) => void;
  onError?: (error: Error) => void;
}

// The guest variant carries no auth — the share token in the URL is the
// authorization.
export type PublicChiefLiveSubscriptionOptions = Omit<ChiefLiveSubscriptionOptions, "authToken" | "projectId">;

export interface ChiefLiveSubscription {
  close: () => void;
}

type StreamCallbacks = Pick<ChiefLiveSubscriptionOptions, "onEvent" | "onError">;

// Streams the ephemeral, in-progress session artifact. The ordinary session
// GET is the durable snapshot and can lag while the host is still recording;
// this endpoint replays the active Redis stream, then tails new events.
export function subscribeToChiefLiveSession(
  sessionId: string,
  options: ChiefLiveSubscriptionOptions
): ChiefLiveSubscription {
  const url = `${getBackendURL()}/chief/session/${encodeURIComponent(sessionId)}/live`;

  return subscribeToStream(
    url,
    () => {
      const token = options.authToken();
      const projectId = options.projectId();
      if (!token || !projectId) {
        throw new Error("Auth or project scope is not ready for the live session.");
      }
      return {
        Authorization: `Bearer ${token}`,
        "X-Project-Id": projectId,
      };
    },
    options
  );
}

// Streams a shared session's live events for an anonymous share-link viewer.
// No bearer or tenancy headers — the share token is the authorization, and the
// server filters the stream to the guest-safe event types.
export function subscribeToPublicChiefLiveSession(
  shareToken: string,
  options: PublicChiefLiveSubscriptionOptions
): ChiefLiveSubscription {
  const url = `${getBackendURL()}/public-live-sessions/${encodeURIComponent(shareToken)}/live`;

  return subscribeToStream(url, () => ({}), options);
}

// No attempt ceiling: someone is watching a meeting happen, so the stream keeps
// trying for as long as the page holds it open.
function subscribeToStream(
  url: string,
  buildHeaders: () => Record<string, string>,
  callbacks: StreamCallbacks
): ChiefLiveSubscription {
  return subscribeToSSE({
    url,
    buildHeaders,
    onData: (data) => {
      try {
        const parsed = ChiefLiveEventSchema.safeParse(JSON.parse(data));
        if (!parsed.success) {
          callbacks.onError?.(new Error(`Invalid live session event: ${parsed.error.message}`));
          return;
        }
        callbacks.onEvent(parsed.data);
      } catch (error) {
        callbacks.onError?.(error instanceof Error ? error : new Error("Failed to parse a live session event."));
      }
    },
    onError: callbacks.onError,
  });
}
